<template>
  <div>

    <!-- List with data -->
    <div v-if="broadcasts.length > 0">

      <!-- Column header row -->
      <div class="broadcast-list">
        <div class="bc-row">
          <div :class="isDraft ? 'bc-col-name--draft' : 'bc-col-name'">
            <p class="sub-title ion-no-margin fontHeader">Broadcast Details</p>
          </div>
          <!-- Show Full Channel -->
          <div v-if="showFullChannel" class="bc-col-channel">
            <p class="sub-title ion-no-margin fontHeader">Primary Channel</p>
          </div>
          <div v-if="showTimestamps" :class="isDraft ? 'bc-col-date--draft' : 'bc-col-date'">
            <p class="sub-title ion-no-margin fontHeader">Created On</p>
          </div>
          <template v-if="!isDraft">
            <div v-if="showTimestamps" class="bc-col-date">
              <p class="sub-title ion-no-margin fontHeader">Executed on</p>
            </div>
            <div v-if="showStatus" class="bc-col-status">
              <p class="sub-title ion-no-margin fontHeader">Status</p>
            </div>
          </template>
          <div v-if="showActions" class="bc-col-actions">
            <p class="sub-title ion-no-margin fontHeader">Actions</p>
          </div>
        </div>
      </div>

      <!-- Broadcast rows -->
      <div class="broadcast-info">
        <div>
          <ViewBroadcast
            v-for="(broadcast, bIndex) in broadcasts"
            :key="broadcast.id"
            :broadcast="broadcast"
            :index="bIndex"
            :isDraft="isDraft"
            :showActions="showActions"
            :showFullChannel="showFullChannel"
            :showTimestamps="showTimestamps"
            :showStatus="showStatus"
            @view-edit="emit('view-edit', $event)"
            @delete="emit('delete', $event)"
            @duplicate="emit('duplicate', $event)"
            @download-outcome="emit('download-outcome', $event)"
          />
        </div>
      </div>

      <!-- Load more -->
      <div v-if="canLoadMore && !isLoadingMore && broadcasts.length > 0 && !filter" class="text-center marginTop">
        <button class="btn-load-more" @click="emit('load-more')">
          {{ isDraft ? 'Load more draft broadcasts...' : 'Load more broadcasts...' }}
        </button>
      </div>
      <div v-if="isLoadingMore" class="text-center marginTop">
        <VProgressRing connotation="cta" />
      </div>

    </div>

    <!-- Loading skeleton state — shown first; takes priority over other empty states -->
    <div class="text-center emptyBroadcast" v-if="broadcasts.length === 0 && isLoadingMore">
      <div>
        <img src="@/resources/images/empty_broadcast.svg" class="filterImg" alt="Empty broadcast" />
        <h5>Fetching {{ isDraft ? 'draft ' : '' }}broadcasts...</h5>
      </div>
    </div>

    <!-- Filter returned no results -->
    <div class="text-center emptyBroadcast" v-else-if="filter && filter.length && broadcasts.length === 0">
      <div>
        <img src="@/resources/images/empty_broadcast.svg" class="filterImg" alt="Empty broadcast" />
        <h5>
          No {{ isDraft ? 'draft ' : '' }}broadcasts matching this filter!
          <a @click.prevent="emit('clear-filter')">Clear filter</a>
        </h5>
      </div>
    </div>

    <!-- Truly empty — no broadcasts exist -->
    <div
      class="text-center emptyBroadcast"
      v-else-if="broadcasts.length === 0 && (!filter || !filter.length)"
    >
      <div>
        <img src="@/resources/images/empty_broadcast.svg" class="filterImg" alt="Empty broadcast" />
        <h2 class="segmentTitle ion-no-margin">Welcome to Broadcast</h2>
        <p class="ion-no-margin segmentSubTitle">
          Send messages to people who have interacted with you in the past.
        </p>
        <VButton connotation="cta" appearance="filled" :label="isDraft ? 'Create Draft broadcast' : 'Create broadcast'" icon="arrow-right-line" icon-trailing @click="emit('create')"></VButton>
      </div>
    </div>

  </div>
</template>

<script setup>
import ViewBroadcast from './ViewBroadcast.vue';


defineProps({
  broadcasts: { type: Array, required: true },
  isDraft: { type: Boolean, default: false },
  canLoadMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
  filter: { type: String, default: '' },
  showActions: { type: Boolean, default: true },
  showFullChannel: { type: Boolean, default: false },
  showTimestamps: { type: Boolean, default: true },
  showStatus: { type: Boolean, default: true },
});

const emit = defineEmits([
  'load-more',
  'view-edit',
  'delete',
  'duplicate',
  'download-outcome',
  'clear-filter',
  'create',
]);
</script>

<style>
/* ── Broadcast list row / column layout ─────────────────────────────── */
/* Non-scoped so classes apply to child ViewBroadcast rows too */
.bc-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
}

/* Desktop column widths (12-col grid equivalents) */
.bc-col-name        { flex: 0 0 25%;    min-width: 0; } /* size 3/12 */
.bc-col-name--draft { flex: 0 0 50%;    min-width: 0; } /* size 6/12 */
.bc-col-channel     { flex: 0 0 12.5%;  min-width: 0; } /* size 1.5/12 */
.bc-col-date        { flex: 0 0 16.67%; min-width: 0; } /* size 2/12 */
.bc-col-date--draft { flex: 0 0 33.33%; min-width: 0; } /* size 4/12 */
.bc-col-status      { flex: 0 0 16.67%; min-width: 0; } /* size 2/12 */
.bc-col-actions     { flex: 0 0 16.67%; min-width: 0; display: flex; align-items: center; justify-content: flex-end; } /* size 2/12 */
.bc-col-actions-mobile { display: none; }

@media (max-width: 991px) {
  .bc-col-channel,
  .bc-col-date,
  .bc-col-date--draft,
  .bc-col-status,
  .bc-col-actions {
    display: none;
  }

  .bc-col-name,
  .bc-col-name--draft {
    flex: 0 0 66.67%;
  }

  .bc-col-actions-mobile {
    display: block;
    flex: 0 0 16.67%;
    min-width: 0;
  }
}
</style>

<style scoped>
.broadcast-list {
  background: var(--white);
  padding: 20px;
  border-bottom: 1px solid var(--background-primary);
  border-radius: 5px 5px 0px 0px;
}

.broadcast-info {
  border-radius: 0px 0px 5px 5px;
  background: var(--white);
}

.filterImg {
  max-width: 200px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

.emptyBroadcast {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 130px auto;
}

.marginTop {
  margin-top: 20px;
}

.headerLeft {
  padding-left: 30px;
}

.btn-load-more {
  background: none;
  border: 1px solid var(--purple);
  color: var(--purple);
  padding: 8px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
}
.btn-load-more:hover {
  background: var(--purple-opac);
}
.fontHeader{
  font-weight: 700;
}
</style>
